import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { MlbSportId } from '../../enums/mlb-stats-api/base.enum';
import { PitchListOwner } from '../../enums/pitch-list.enums';
import {
  PitcherHand,
  PitcherPosition,
  PitchType,
  ReferenceListType,
  TrainingStatus,
} from '../../enums/pitches.enums';
import { IMachine } from '../i-machine';
import { ITeam } from '../i-team';
import { IUser } from '../i-user';
import { MongoChildRecord } from '../mongo/_base';

export const FOLDER_SEPARATOR_REGEX = /[/]+/g;

export const FOLDER_SEPARATOR = '/';

export const safeFolder = (rawFolder?: string): string => {
  if (rawFolder && typeof rawFolder === 'string') {
    return rawFolder
      .split(FOLDER_SEPARATOR_REGEX)
      .map((p) => p.trim())
      .filter((p) => !!p)
      .join(FOLDER_SEPARATOR);
  }

  return '';
};

export interface ITrainingDict {
  [machineID: string]: TrainingStatus;
}

// populated when a list is created via game data > bulk repertoire importer
// used for identifying which list to auto-populate the bulk importer > pitch list input
interface IMlbRepertoire {
  season: number;
  sportId: MlbSportId;
  playerPk: number;
}

export interface IPitchList extends MongoChildRecord {
  _parent_def: PitchListOwner;

  reference_type?: ReferenceListType;

  folder: string;
  name: string;

  pitcher?: Nullable<string>;
  team?: Nullable<string>;
  // used for identifying pitchers w/o MLB Stats API
  customID?: Nullable<string>;

  hand?: Nullable<PitcherHand>;
  position?: PitcherPosition;

  /** for filtering which lists show up for a user's session
   * true: only loaded when the user has super access
   * false (or empty): does not require super access to load
   * */
  super: boolean;

  /** for toggling on/off reference lists to be shown in model building UI */
  live?: boolean;

  /** for choosing what is selected by default */
  model_builder_default?: boolean;

  description_md?: string;

  // mixin via the client side
  _mx_summary?: IPitchListSummary;

  mlb_repertoire?: Nullable<IMlbRepertoire>;

  // for Game Data player picker
  teamPk?: Nullable<number>;
}

/**
 * options shown to the current user when attempting reassignment
 */
export interface IReassignOptions {
  teams: ITeam[];
  machines: IMachine[];
  users: IUser[];
}

export const DEFAULT_REASSIGN_OPTIONS: IReassignOptions = {
  teams: [],
  machines: [],
  users: [],
};

export interface IPitchListPutManyRequest {
  filter: Partial<IPitchList>;
  update: Partial<IPitchList>;

  /** if provided, _id of lists matching filter param will need to be provided here to be reassigned */
  filterLists?: IPitchList[];
}

export interface IRenameFolderRequest {
  reference_type: ReferenceListType | undefined;
  _parent_id: string;
  fromFolder: string;
  toFolder: string;
}

export interface IPitchListSummary {
  training_status: TrainingStatus;

  types: PitchType[];

  // 0 from server, filled in by lists context
  trained: number;
  // 0 from server, filled in by lists context
  trained_percent: number;

  bs_hashes: string[];
  missing_hashes: number;
  total: number;
}

export const DEFAULT_MX_SUMMARY: IPitchListSummary = {
  training_status: TrainingStatus.Not,
  types: [],
  trained: 0,
  trained_percent: 0,
  bs_hashes: [],
  missing_hashes: 0,
  total: 0,
};

export interface IPitchListSummaryDict {
  [listID: string]: IPitchListSummary | undefined;
}
